import { wei } from '@kwenta/wei';
import { hexToBigInt } from 'viem';
import { optimism } from 'viem/chains';
import * as sdkErrors from '../constants/errors.js';
import { AGGREGATE_ASSET_KEY, KWENTA_TRACKING_CODE } from '../constants/futures.js';
import { ZERO_BIG_INT, ZERO_WEI } from '../constants/number.js';
import { SECONDS_PER_DAY } from '../constants/period.js';
import { DEFAULT_NUMBER_OF_FUTURES_FEE, EPOCH_CONFIGS, REFERRAL_PROGRAM_END_EPOCH, REFERRAL_PROGRAM_START_EPOCH, STAKING_V2_REWARDS_CUTOFF_EPOCH, STAKING_V2_REWARDS_END_EPOCH, } from '../constants/staking.js';
import SnxPerpsV2Queries from '../queries/snxPerpsV2.js';
import { queryOperatorsByOwner } from '../queries/staking.js';
import { formatDate, formatTruncatedDuration } from '../utils/date.js';
import { awsClient } from '../utils/files.js';
import { calculateFeesForAccount, calculateTotalFees, getEpochPeriod, getStakingGqlEndpoint, } from '../utils/index.js';
import { weiFromWei } from '../utils/number.js';
export default class KwentaTokenService {
    sdk;
    queries;
    constructor(sdk) {
        this.sdk = sdk;
        this.queries = new SnxPerpsV2Queries(sdk.context.subgraphUrls.snx_v2_op);
    }
    get stakingGqlEndpoint() {
        return getStakingGqlEndpoint(10);
    }
    get client() {
        return this.sdk.context.clients[10] ?? this.sdk.context.signerPublicClient;
    }
    get contractConfigs() {
        return this.sdk.context.contractConfigs[10];
    }
    switchToOptimismAndWriteContact(params) {
        return this.sdk.transactions.writeContract(params, optimism.id);
    }
    async getStakingV2Data() {
        const { RewardEscrowV2, KwentaStakingRewardsV2, KwentaToken } = this.contractConfigs.kwenta;
        if (!RewardEscrowV2 || !KwentaStakingRewardsV2 || !KwentaToken) {
            throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
        }
        const { walletAddress } = this.sdk.context;
        const [rewardEscrowBalance, stakedNonEscrowedBalance, stakedEscrowedBalance, claimableBalance, feeShareBalance, totalStakedBalance, lastStakedTime, cooldownPeriod, kwentaBalance,] = await this.client.multicall({
            allowFailure: false,
            contracts: [
                {
                    ...RewardEscrowV2,
                    functionName: 'escrowedBalanceOf',
                    args: [walletAddress],
                },
                {
                    ...KwentaStakingRewardsV2,
                    functionName: 'nonEscrowedBalanceOf',
                    args: [walletAddress],
                },
                {
                    ...KwentaStakingRewardsV2,
                    functionName: 'escrowedBalanceOf',
                    args: [walletAddress],
                },
                {
                    ...KwentaStakingRewardsV2,
                    functionName: 'earned',
                    args: [walletAddress],
                },
                {
                    ...KwentaStakingRewardsV2,
                    functionName: 'earnedUSDC',
                    args: [walletAddress],
                },
                {
                    ...KwentaStakingRewardsV2,
                    functionName: 'totalSupply',
                },
                {
                    ...KwentaStakingRewardsV2,
                    functionName: 'userLastStakeTime',
                    args: [walletAddress],
                },
                {
                    ...KwentaStakingRewardsV2,
                    functionName: 'cooldownPeriod',
                },
                {
                    ...KwentaToken,
                    functionName: 'balanceOf',
                    args: [walletAddress],
                },
            ],
        });
        return {
            rewardEscrowBalance: wei(rewardEscrowBalance),
            stakedNonEscrowedBalance: wei(stakedNonEscrowedBalance),
            stakedEscrowedBalance: wei(stakedEscrowedBalance),
            claimableBalance: wei(claimableBalance),
            feeShareBalance: wei(feeShareBalance),
            totalStakedBalance: wei(totalStakedBalance),
            stakedResetTime: Number(lastStakedTime) + Number(cooldownPeriod),
            kwentaBalance: wei(kwentaBalance),
        };
    }
    async getEscrowV2Data() {
        const { RewardEscrowV2 } = this.contractConfigs.kwenta;
        if (!RewardEscrowV2) {
            throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
        }
        const { walletAddress } = this.sdk.context;
        const schedules = await this.client.readContract({
            ...RewardEscrowV2,
            functionName: 'getVestingSchedules',
            args: [walletAddress, ZERO_BIG_INT, DEFAULT_NUMBER_OF_FUTURES_FEE],
        });
        const vestingSchedules = schedules
            .filter((schedule) => schedule.escrowAmount > ZERO_BIG_INT)
            .sort((a, b) => Number(a.endTime) - Number(b.endTime));
        const { escrowData, totalVestable } = vestingSchedules.reduce((acc, next, _i) => {
            const date = Number(next.endTime) * 1000;
            acc.totalVestable = acc.totalVestable.add(wei(next.escrowAmount));
            acc.escrowData.push({
                id: Number(next.entryID),
                date: formatDate(date),
                time: formatTruncatedDuration(0),
                vestable: wei(next.escrowAmount),
                amount: wei(next.escrowAmount),
                fee: ZERO_WEI,
                status: 'Vested',
                version: 2,
            });
            return acc;
        }, { escrowData: [], totalVestable: wei(0) });
        return { escrowData, totalVestable };
    }
    async claimStakingRewardsV2() {
        const { KwentaStakingRewardsV2 } = this.contractConfigs.kwenta;
        if (!KwentaStakingRewardsV2) {
            throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
        }
        return this.switchToOptimismAndWriteContact({
            ...KwentaStakingRewardsV2,
            functionName: 'getReward',
        });
    }
    async vestTokenV2(ids) {
        const { RewardEscrowV2 } = this.contractConfigs.kwenta;
        if (!RewardEscrowV2) {
            throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
        }
        return this.switchToOptimismAndWriteContact({
            ...RewardEscrowV2,
            functionName: 'vest',
            args: [ids],
        });
    }
    unstakeKwentaV2(amount) {
        return this.performStakeAction('unstake', amount, { escrow: false, version: 2 });
    }
    unstakeEscrowedKwentaV2(amount) {
        return this.performStakeAction('unstake', amount, { escrow: true, version: 2 });
    }
    async getClaimableOpReferralRewards() {
        const { MultipleMerkleDistributorOpReferral } = this.contractConfigs.kwenta;
        const { walletAddress } = this.sdk.context;
        if (!MultipleMerkleDistributorOpReferral) {
            throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
        }
        const epochPeriod = getEpochPeriod(EPOCH_CONFIGS.opReferral);
        if (epochPeriod < 0) {
            return { claimableRewards: [], totalRewards: wei(0) };
        }
        const periods = Array.from(new Array(Number(epochPeriod)), (_, i) => i);
        const fileNames = periods.map((i) => `epoch-${i}-op-referral.json`);
        const responses = await Promise.all(fileNames.map(async (fileName, period) => {
            const response = await awsClient.get(fileName);
            return { ...response.data, period };
        }));
        const rewards = responses
            .map((d) => {
            const reward = d.claims[walletAddress];
            if (reward) {
                return {
                    index: reward.index,
                    account: walletAddress,
                    amount: weiFromWei(reward.amount),
                    merkleProof: reward.proof,
                    epoch: d.period,
                };
            }
            return null;
        })
            .filter((x) => !!x);
        const claimed = (await this.client.multicall({
            allowFailure: false,
            contracts: rewards.map((reward) => ({
                ...MultipleMerkleDistributorOpReferral,
                functionName: 'isClaimed',
                args: [reward.index, reward.epoch],
            })),
        }));
        const { totalRewards, claimableRewards } = rewards.reduce((acc, next, i) => {
            if (!claimed[i]) {
                acc.claimableRewards.push(next);
                acc.totalRewards = acc.totalRewards.add(next.amount);
            }
            return acc;
        }, { claimableRewards: [], totalRewards: wei(0) });
        return { claimableRewards, totalRewards };
    }
    async getClaimableKwentaRewards(epochPeriod) {
        const { MultipleMerkleDistributorStakingV2 } = this.contractConfigs.kwenta;
        const { walletAddress } = this.sdk.context;
        if (!MultipleMerkleDistributorStakingV2) {
            throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
        }
        const periods = Array.from(new Array(Number(epochPeriod)), (_, i) => i);
        const adjustedPeriods = periods.slice(STAKING_V2_REWARDS_CUTOFF_EPOCH, STAKING_V2_REWARDS_END_EPOCH);
        const fileNames = adjustedPeriods.map((i) => `epoch-${i}.json`).filter(Boolean);
        const responses = await Promise.all(fileNames.map(async (fileName, index) => {
            try {
                const response = await awsClient.get(fileName);
                const period = index + STAKING_V2_REWARDS_CUTOFF_EPOCH;
                return { ...response.data, period };
            }
            catch (err) {
                this.sdk.context.logError(err);
                return null;
            }
        }));
        const rewards = responses
            .filter(Boolean)
            .map((d) => {
            const reward = d.claims[walletAddress];
            if (reward) {
                return {
                    index: reward.index,
                    account: walletAddress,
                    amount: wei(hexToBigInt(reward.amount)),
                    merkleProof: reward.proof,
                    epoch: d.period,
                };
            }
            return null;
        })
            .filter((x) => !!x);
        const claimed = (await this.client.multicall({
            allowFailure: false,
            contracts: rewards.map((reward) => {
                return {
                    ...MultipleMerkleDistributorStakingV2,
                    functionName: 'isClaimed',
                    args: [reward.index, reward.epoch],
                };
            }),
        }));
        const { totalRewards, claimableRewards } = rewards.reduce((acc, next, i) => {
            if (!claimed[i]) {
                acc.claimableRewards.push(next);
                acc.totalRewards = acc.totalRewards.add(next.amount);
            }
            return acc;
        }, { claimableRewards: [], totalRewards: wei(0) });
        return { claimableRewards, totalRewards };
    }
    async getKwentaRewardsByEpoch(epochPeriod) {
        const { walletAddress } = this.sdk.context;
        const fileName = `epoch-${epochPeriod}.json`;
        try {
            const response = await awsClient.get(fileName);
            const rewards = response.data.claims[walletAddress];
            return rewards ? weiFromWei(rewards.amount) : ZERO_WEI;
        }
        catch (err) {
            this.sdk.context.logError(err);
            return ZERO_WEI;
        }
    }
    async getKwentaRewardsByTraders(epochPeriod, traders) {
        const periods = Array.from(new Array(Number(epochPeriod)), (_, i) => i);
        const adjustedPeriods = periods.slice(REFERRAL_PROGRAM_START_EPOCH, REFERRAL_PROGRAM_END_EPOCH);
        const fileNames = adjustedPeriods.map((i) => `epoch-${i}.json`);
        try {
            const responses = await Promise.all(fileNames.map(async (fileName) => {
                try {
                    const response = await awsClient.get(fileName);
                    return { ...response.data };
                }
                catch (err) {
                    this.sdk.context.logError(err);
                    return null;
                }
            }));
            const rewards = traders.map((walletAddress) => {
                const lowerCaseWalletAddress = walletAddress.toLowerCase();
                return responses
                    .filter(Boolean)
                    .map(({ claims }) => {
                    const lowerCaseClaims = Object.fromEntries(Object.entries(claims).map(([key, value]) => [key.toLowerCase(), value]));
                    const reward = lowerCaseClaims[lowerCaseWalletAddress];
                    return reward ? wei(reward.amount) : wei('0');
                })
                    .reduce((acc, amount) => (amount ? acc.add(weiFromWei(amount)) : acc), ZERO_WEI);
            });
            return rewards
                .flat()
                .reduce((total, next) => (next ? total.add(weiFromWei(next)) : total), ZERO_WEI);
        }
        catch (err) {
            this.sdk.context.logError(err);
            return ZERO_WEI;
        }
    }
    async claimKwentaRewards(claimableRewards) {
        const { MultipleMerkleDistributorPerpsV2 } = this.contractConfigs.kwenta;
        if (!MultipleMerkleDistributorPerpsV2) {
            throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
        }
        return this.sdk.transactions.writeContract({
            ...MultipleMerkleDistributorPerpsV2,
            functionName: 'claimMultiple',
            args: [
                claimableRewards.map((r) => ({
                    ...r,
                    amount: r.amount.toBigInt(),
                    index: BigInt(r.index),
                    epoch: BigInt(r.epoch),
                })),
            ],
        });
    }
    async claimMultipleAllRewards(claimableRewards) {
        const { BatchClaimer, MultipleMerkleDistributorStakingV2 } = this.contractConfigs.kwenta;
        if (!BatchClaimer || !MultipleMerkleDistributorStakingV2) {
            throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
        }
        return this.switchToOptimismAndWriteContact({
            ...BatchClaimer,
            functionName: 'claimMultiple',
            args: [
                [MultipleMerkleDistributorStakingV2.address],
                claimableRewards.map((rewards) => rewards.map((r) => ({
                    ...r,
                    amount: r.amount.toBigInt(),
                    index: BigInt(r.index),
                    epoch: BigInt(r.epoch),
                }))),
            ],
        });
    }
    async claimOpRewards(claimableRewards, isSnx = false) {
        const { MultipleMerkleDistributorOp, MultipleMerkleDistributorSnxOp } = this.contractConfigs.kwenta;
        if (!MultipleMerkleDistributorOp || !MultipleMerkleDistributorSnxOp) {
            throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
        }
        const contract = isSnx ? MultipleMerkleDistributorSnxOp : MultipleMerkleDistributorOp;
        return this.sdk.transactions.writeContract({
            ...contract,
            functionName: 'claimMultiple',
            args: [
                claimableRewards.map((r) => ({
                    ...r,
                    amount: r.amount.toBigInt(),
                    index: BigInt(r.index),
                    epoch: BigInt(r.epoch),
                })),
            ],
        });
    }
    async claimOpReferralRewards(claimableRewards) {
        const { MultipleMerkleDistributorOpReferral } = this.contractConfigs.kwenta;
        if (!MultipleMerkleDistributorOpReferral) {
            throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
        }
        return this.switchToOptimismAndWriteContact({
            ...MultipleMerkleDistributorOpReferral,
            functionName: 'claimMultiple',
            args: [
                claimableRewards.map((r) => ({
                    ...r,
                    amount: r.amount.toBigInt(),
                    index: BigInt(r.index),
                    epoch: BigInt(r.epoch),
                })),
            ],
        });
    }
    async getFuturesFee(start, end, chainId) {
        const response = await this.queries.getFuturesAggregateStats({
            chainId,
            first: Number(DEFAULT_NUMBER_OF_FUTURES_FEE),
            where: {
                asset: AGGREGATE_ASSET_KEY,
                period: SECONDS_PER_DAY,
                timestamp_gte: start,
                timestamp_lt: end,
            },
            orderDirection: 'desc',
            orderBy: 'timestamp',
        });
        return response ? calculateTotalFees(response) : wei(0);
    }
    async getFuturesFeeForAccount(account, start, end, chainId) {
        if (!account)
            return wei(0);
        const response = await this.queries.getFuturesTrades({
            chainId,
            first: Number(DEFAULT_NUMBER_OF_FUTURES_FEE),
            where: {
                account: account,
                timestamp_gt: start,
                timestamp_lt: end,
                trackingCode: KWENTA_TRACKING_CODE,
            },
            orderDirection: 'desc',
            orderBy: 'timestamp',
        });
        return response ? calculateFeesForAccount(response) : wei(0);
    }
    async performStakeAction(action, amount, options = { escrow: false, version: 1 }) {
        const { KwentaStakingRewardsV2 } = this.contractConfigs.kwenta;
        if (!KwentaStakingRewardsV2) {
            throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
        }
        return this.switchToOptimismAndWriteContact({
            ...KwentaStakingRewardsV2,
            functionName: `${action}${options?.escrow ? 'Escrow' : ''}`,
            args: [amount],
        });
    }
    async getApprovedOperators() {
        const { walletAddress } = this.sdk.context;
        return queryOperatorsByOwner(this.sdk, walletAddress);
    }
    async transferFrom(from, to, id) {
        const { RewardEscrowV2 } = this.contractConfigs.kwenta;
        if (!RewardEscrowV2) {
            throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
        }
        return this.switchToOptimismAndWriteContact({
            ...RewardEscrowV2,
            functionName: 'transferFrom',
            args: [from, to, id],
        });
    }
    async bulkTransferFrom(from, to, ids) {
        const { RewardEscrowV2 } = this.contractConfigs.kwenta;
        if (!RewardEscrowV2) {
            throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
        }
        return this.switchToOptimismAndWriteContact({
            ...RewardEscrowV2,
            functionName: 'bulkTransferFrom',
            args: [from, to, ids],
        });
    }
}
//# sourceMappingURL=kwentaToken.js.map